// Helpers for the sidebar's "keep only the last few folders open" behaviour. Rendering many
// expanded folders at once is slow, so each time the sidebar is shown (page load, or the mobile
// drawer re-appearing) all but the most recently opened folders are collapsed.

// Most named folders a user can create.
export const MAX_FOLDERS = 25;
// How many folders stay expanded when the sidebar is shown.
export const MAX_FOLDERS_OPEN_ON_SHOW = 3;
// How many folder names of open-history are kept (persisted); only the tail is ever used.
export const MAX_TRACKED_FOLDER_HISTORY = 20;

// A folder with no stored state counts as open — that is the render default.
export const isFolderOpen = (expanded: Record<string, boolean>, name: string) => expanded[name] ?? true;

/**
 * Picks the folders that stay expanded, most recently opened first.
 *
 * @param openFolders the currently expanded folder names
 * @param openOrder folder names in the order they were expanded, most recent last
 * @param selectedFolder folder of the currently selected feed; always kept so the feed being
 *                       read can't vanish from the tree
 */
export const pickFoldersToKeepOpen = (openFolders: string[], openOrder: string[], selectedFolder?: string): Set<string> => {
  if (openFolders.length <= MAX_FOLDERS_OPEN_ON_SHOW) return new Set(openFolders);
  const rank = (name: string) => name == selectedFolder ? Infinity : openOrder.indexOf(name);
  // Never-opened folders rank last; ties keep the topmost ones in the (alphabetical) tree order.
  return new Set([...openFolders]
    .sort((a, b) => rank(b) - rank(a) || a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .slice(0, MAX_FOLDERS_OPEN_ON_SHOW));
};

/** Updated open-history after a folder was expanded (`isOpen`) or collapsed. */
export const updateFolderOpenOrder = (openOrder: string[], name: string, isOpen: boolean): string[] => {
  const rest = openOrder.filter(n => n != name);
  return isOpen ? [...rest, name].slice(-MAX_TRACKED_FOLDER_HISTORY) : rest;
};
